import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useTimetableTypesStore } from '../../stateManagers/store/useTimetableTypesStore';
import { useTimeSlotsStore } from '../../stateManagers/store/useTimeSlotsStore';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import { useDefaultDataStore } from '../../stateManagers/store/useDefaultDataStore';

interface EditableSlot {
  id: string;          // real backend id once persisted, or a local `new-N` id before save
  periodNumber: number;
  periodLabel: string;
  startTime: string;   // "HH:MM" for the input
  endTime: string;
  slotType: string;
  isNew: boolean;       // not yet persisted — shows a Save action instead of Edit/Delete
  isEditing: boolean;   // persisted, but currently unlocked for editing
  saving: boolean;
}

interface TimetableTab {
  id: string;
  name: string;
  session: string;
  timeSlots: EditableSlot[];
}

// Must stay in sync with the backend's `oneof=period break power_nap workbook activities`
// validation on CreateTimeSlot / UpdateTimeSlot / BulkCreateTimeSlots.
const slotTypeOptions = [
  { value: 'period', label: 'Period', icon: 'pi-book' },
  { value: 'break', label: 'Break', icon: 'pi-coffee' },
  { value: 'power_nap', label: 'Power Nap', icon: 'pi-moon' },
  { value: 'workbook', label: 'Workbook', icon: 'pi-file-edit' },
  { value: 'activities', label: 'Activities', icon: 'pi-star' },
];

let tempIdCounter = 0;
const nextTempId = () => `new-${Date.now()}-${tempIdCounter++}`;

const toHHMM = (t: string) => (t ? t.substring(0, 5) : '');
const toHHMMSS = (t: string) => (t ? `${t}:00` : '');

export default function SetupTimetable() {
  const activeSchool = useDefaultDataStore((state) => state.active_school);
  const { timetableTypes, loading, error, fetchTimetableTypes } = useTimetableTypesStore();
  const { timeSlots, loading: slotsLoading, error: slotsError, fetchTimeSlots } = useTimeSlotsStore();
  const [timetables, setTimetables] = useState<TimetableTab[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const toast = useRef<Toast | null>(null);

  const styles = `
    @keyframes fadeInSlide {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .custom-dropdown .p-dropdown {
      border: 1px solid #e5e7eb !important;
      border-radius: 8px !important;
      height: 40px !important;
    }

    .custom-dropdown .p-dropdown .p-dropdown-trigger {
      width: 40px !important;
    }

    .custom-dropdown .p-dropdown .p-dropdown-label {
      font-size: 0.8rem !important;
    }

    .p-dropdown-panel .p-dropdown-item {
      font-size: 0.8rem !important;
    }

    .p-tabview .p-tabview-nav {
      background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%) !important;
      border: none !important;
      border-radius: 16px !important;
      padding: 6px 8px !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
    }

    .p-tabview .p-tabview-nav li .p-tabview-nav-link {
      background: transparent !important;
      border: none !important;
      color: #64748b !important;
      border-radius: 12px !important;
      padding: 12px 20px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      font-weight: 500 !important;
      transform: scale(1) !important;
    }

    .p-tabview .p-tabview-nav li .p-tabview-nav-link:hover {
      background: rgba(59, 130, 246, 0.15) !important;
      color: #2855ae !important;
      transform: translateY(-2px) scale(1.02) !important;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2) !important;
    }

    .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
      background: linear-gradient(135deg, #2855ae 0%, #3b82f6 100%) !important;
      color: white !important;
      box-shadow: 0 4px 12px rgba(40, 85, 174, 0.3) !important;
      transform: scale(1) !important;
    }

    .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link:hover {
      box-shadow: 0 6px 16px rgba(40, 85, 174, 0.4) !important;
      transform: translateY(-2px) scale(1) !important;
    }

    .p-tabview .p-tabview-panels {
      background: transparent !important;
      border: none !important;
      padding: 24px 0 0 0 !important;
    }
  `;

  // Initial fetch of timetable types
  useEffect(() => {
    fetchTimetableTypes();
  }, [fetchTimetableTypes]);

  // Initialize tabs when timetableTypes load
  useEffect(() => {
    if (timetableTypes && timetableTypes.length > 0 && timetables.length === 0) {
      const initial = timetableTypes.map((type) => ({
        id: type.id,
        name: type.name,
        session: type.session,
        timeSlots: [],
      }));
      setTimetables(initial);
      setActiveIndex(0);
    }
  }, [timetableTypes, timetables.length]);

  // Fetch slots for the active tab
  useEffect(() => {
    const activeTimetable = timetables[activeIndex];
    if (activeTimetable) {
      fetchTimeSlots(activeTimetable.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, timetables.length]);

  // Sync fetched slots from the store into the active tab.
  // Preserves any local unsaved (isNew) rows the user hasn't saved yet.
  useEffect(() => {
    if (timetables.length === 0) return;
    const activeTimetable = timetables[activeIndex];
    if (!activeTimetable) return;

    const relevant = timeSlots
      .filter((s) => s.timetable_type_id === activeTimetable.id)
      .sort((a, b) => a.period_number - b.period_number)
      .map(
        (s): EditableSlot => ({
          id: s.id,
          periodNumber: s.period_number,
          periodLabel: s.period_label,
          startTime: toHHMM(s.start_time),
          endTime: toHHMM(s.end_time),
          slotType: s.slot_type,
          isNew: false,
          isEditing: false,
          saving: false,
        })
      );

    setTimetables((prev) => {
      const current = prev[activeIndex];
      if (!current) return prev;
      const unsavedRows = current.timeSlots.filter((s) => s.isNew);
      const merged = [...relevant, ...unsavedRows];
      const isDifferent = JSON.stringify(current.timeSlots.filter((s) => !s.isNew)) !== JSON.stringify(relevant);
      if (!isDifferent && unsavedRows.length === current.timeSlots.filter((s) => s.isNew).length) return prev;
      return prev.map((tab, idx) => (idx === activeIndex ? { ...tab, timeSlots: merged } : tab));
    });
  }, [timeSlots, activeIndex, timetables.length]);

  useEffect(() => {
    if (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: error, life: 3000 });
    }
  }, [error]);

  useEffect(() => {
    if (slotsError) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error Loading Time Slots',
        detail: slotsError,
        life: 3000,
      });
    }
  }, [slotsError]);

  const handleTabChange = (e: any) => {
    setActiveIndex(e.index);
  };

  const addTimeSlot = (tabId: string, event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    setTimetables((prev) =>
      prev.map((tab) => {
        if (tab.id !== tabId) return tab;

        if (tab.timeSlots.length >= 10) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Limit Reached',
            detail: 'Maximum 10 periods allowed.',
            life: 3000,
          });
          return tab;
        }

        const maxPeriod = Math.max(...tab.timeSlots.map((s) => s.periodNumber), 0);
        const newSlot: EditableSlot = {
          id: nextTempId(),
          periodNumber: maxPeriod + 1,
          periodLabel: `P${maxPeriod + 1}`,
          startTime: '',
          endTime: '',
          slotType: '',
          isNew: true,
          isEditing: false,
          saving: false,
        };

        return { ...tab, timeSlots: [...tab.timeSlots, newSlot] };
      })
    );
  };

  const updateLocalField = (
    tabId: string,
    rowId: string,
    field: 'periodLabel' | 'startTime' | 'endTime' | 'slotType',
    value: string
  ) => {
    setTimetables((prev) =>
      prev.map((tab) =>
        tab.id === tabId
          ? { ...tab, timeSlots: tab.timeSlots.map((s) => (s.id === rowId ? { ...s, [field]: value } : s)) }
          : tab
      )
    );
  };

  const startEditing = (tabId: string, rowId: string) => {
    setTimetables((prev) =>
      prev.map((tab) =>
        tab.id === tabId
          ? { ...tab, timeSlots: tab.timeSlots.map((s) => (s.id === rowId ? { ...s, isEditing: true } : s)) }
          : tab
      )
    );
  };

  const cancelEditing = (tabId: string) => {
    // Re-fetch from the store to discard any unsaved edits on persisted rows.
    const tab = timetables.find((t) => t.id === tabId);
    if (tab) fetchTimeSlots(tab.id);
  };

  const setRowSaving = (tabId: string, rowId: string, saving: boolean) => {
    setTimetables((prev) =>
      prev.map((tab) =>
        tab.id === tabId
          ? { ...tab, timeSlots: tab.timeSlots.map((s) => (s.id === rowId ? { ...s, saving } : s)) }
          : tab
      )
    );
  };

  const isRowComplete = (slot: EditableSlot) =>
    slot.periodLabel.trim() !== '' && slot.startTime !== '' && slot.endTime !== '' && slot.slotType !== '';

  const validateRange = (slot: EditableSlot) => {
    if (slot.endTime <= slot.startTime) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Invalid Time Range',
        detail: 'End time must be after start time.',
        life: 3000,
      });
      return false;
    }
    return true;
  };

  // Save a brand-new (unsaved) row via a single POST /timeslots
  const saveNewSlot = async (tabId: string, rowId: string) => {
    const tab = timetables.find((t) => t.id === tabId);
    const slot = tab?.timeSlots.find((s) => s.id === rowId);
    if (!tab || !slot || !isRowComplete(slot)) return;
    if (!validateRange(slot)) return;

    if (!activeSchool?.school_id) {
      toast.current?.show({
        severity: 'error',
        summary: 'No School Selected',
        detail: 'Select a school before adding time slots.',
        life: 4000,
      });
      return;
    }

    setRowSaving(tabId, rowId, true);
    try {
      const response = await apiClient.post('/timeslots', {
        school_id: activeSchool?.school_id,
        session: tab.session,
        timetable_type_id: tab.id,
        period_number: slot.periodNumber,
        period_label: slot.periodLabel.trim(),
        start_time: toHHMMSS(slot.startTime),
        end_time: toHHMMSS(slot.endTime),
        slot_type: slot.slotType,
      });

      toast.current?.show({
        severity: 'success',
        summary: 'Saved',
        detail: response.data?.message || 'Time slot added successfully.',
        life: 3000,
      });

      await fetchTimeSlots(tab.id);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Save Failed',
        detail: err.response?.data?.message || 'Could not save time slot',
        life: 4000,
      });
      setRowSaving(tabId, rowId, false);
    }
  };

  // Save changes to an already-persisted row via PUT /timeslots/:id
  const saveEditedSlot = async (tabId: string, rowId: string) => {
    const tab = timetables.find((t) => t.id === tabId);
    const slot = tab?.timeSlots.find((s) => s.id === rowId);
    if (!tab || !slot || !isRowComplete(slot)) return;
    if (!validateRange(slot)) return;

    setRowSaving(tabId, rowId, true);
    try {
      await apiClient.put(`/timeslots/${slot.id}`, {
        period_number: slot.periodNumber,
        period_label: slot.periodLabel.trim(),
        start_time: toHHMMSS(slot.startTime),
        end_time: toHHMMSS(slot.endTime),
        slot_type: slot.slotType,
      });

      toast.current?.show({
        severity: 'success',
        summary: 'Updated',
        detail: 'Time slot updated successfully.',
        life: 3000,
      });

      await fetchTimeSlots(tab.id);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Update Failed',
        detail: err.response?.data?.message || 'Could not update time slot',
        life: 4000,
      });
      setRowSaving(tabId, rowId, false);
    }
  };

  const deleteTimeSlot = (tabId: string, rowId: string, isNew: boolean, label: string) => {
    if (isNew) {
      // Never persisted — just drop it locally.
      setTimetables((prev) =>
        prev.map((tab) => (tab.id === tabId ? { ...tab, timeSlots: tab.timeSlots.filter((s) => s.id !== rowId) } : tab))
      );
      return;
    }

    confirmDialog({
      message: `Deactivate "${label}"? It will be removed from the active schedule.`,
      header: 'Confirm Deactivation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await apiClient.delete(`/timeslots/${rowId}`);
          toast.current?.show({
            severity: 'success',
            summary: 'Deactivated',
            detail: `"${label}" has been deactivated.`,
            life: 3000,
          });
          const tab = timetables.find((t) => t.id === tabId);
          if (tab) await fetchTimeSlots(tab.id);
        } catch (err: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Delete Failed',
            detail: err.response?.data?.message || 'Could not deactivate time slot',
            life: 4000,
          });
        }
      },
    });
  };

  const activeTimetable = timetables[activeIndex];
  const isLoadingSlots = slotsLoading && !!activeTimetable;
  const hasTimeSlots = activeTimetable && activeTimetable.timeSlots.length > 0;

  return (
    <div style={{ minHeight: '100vh', padding: '0px', background: '#fdfdfdff' }}>
      <style>{styles}</style>
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
          borderRadius: '24px',
          padding: '32px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="pi pi-clock" style={{ fontSize: '18px', color: 'white' }}></i>
          </div>
          <div>
            <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '600', margin: '0 0 4px 0' }}>
              Setup Timetable
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span
                style={{
                  background: 'rgba(74, 222, 128, 0.9)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                }}
              >
                {timetables.length} Timetable{timetables.length !== 1 ? 's' : ''}
              </span>
              <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                {new Date().toISOString().split('T')[0]}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '2px solid #dbeafe',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '10px',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '32px',
              height: '32px',
            }}
          >
            <i className="pi pi-info-circle" style={{ fontSize: '14px', color: 'white' }}></i>
          </div>
          <div>
            <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: '600', margin: '0 0 6px 0' }}>
              Configure Time Slots
            </h4>
            <p style={{ color: '#6b7280', fontSize: '13px', margin: 0, lineHeight: '1.6' }}>
              Configure schedules for each timetable type. Add up to 10 periods per timetable and specify
              time slots and types for each period. Each period saves individually as soon as it's filled in.
            </p>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div
          style={{
            background: 'white',
            borderRadius: '20px',
            padding: '48px',
            textAlign: 'center',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          }}
        >
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '48px', color: '#2855ae' }}></i>
          <p style={{ color: '#6b7280', marginTop: '16px', fontSize: '16px', fontWeight: '500' }}>
            Loading timetables...
          </p>
        </div>
      ) : timetables.length === 0 ? (
        <div
          style={{
            background: 'white',
            borderRadius: '20px',
            padding: '48px',
            textAlign: 'center',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          }}
        >
          <i className="pi pi-inbox" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }}></i>
          <h3 style={{ color: '#6b7280', fontSize: '18px', margin: '0 0 8px 0' }}>No Timetables Found</h3>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
            Timetable types will appear here once they are configured.
          </p>
        </div>
      ) : (
        <div
          style={{
            background: 'white',
            borderRadius: '20px',
            padding: '24px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          }}
        >
          <TabView activeIndex={activeIndex} onTabChange={handleTabChange}>
            {timetables.map((timetable) => (
              <TabPanel
                key={timetable.id}
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <i className="pi pi-calendar" style={{ fontSize: '14px' }}></i>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{ fontWeight: '600' }}>{timetable.name}</span>
                      <span style={{ fontSize: '10px', opacity: 0.8 }}>{timetable.session}</span>
                    </div>
                  </div>
                }
              >
                {isLoadingSlots ? (
                  <div style={{ padding: '48px', textAlign: 'center' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '36px', color: '#2855ae' }}></i>
                    <p style={{ color: '#6b7280', marginTop: '16px', fontSize: '14px' }}>Loading time slots...</p>
                  </div>
                ) : !hasTimeSlots ? (
                  <div
                    style={{
                      background: '#f9fafb',
                      borderRadius: '16px',
                      padding: '48px',
                      textAlign: 'center',
                      border: '2px dashed #e5e7eb',
                      marginTop: '20px',
                    }}
                  >
                    <i
                      className="pi pi-calendar-times"
                      style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }}
                    ></i>
                    <h3 style={{ color: '#6b7280', fontSize: '18px', margin: '0 0 8px 0' }}>No Slots Available</h3>
                    <p style={{ color: '#9ca3af', fontSize: '14px', margin: '0 0 20px 0' }}>
                      Add periods to create your timetable schedule.
                    </p>
                    <button
                      type="button"
                      onClick={(e) => addTimeSlot(timetable.id, e)}
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '12px 24px',
                        cursor: 'pointer',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                      }}
                    >
                      <i className="pi pi-plus" style={{ fontSize: '14px' }}></i>
                      Add Period
                    </button>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={(e) => addTimeSlot(timetable.id, e)}
                        disabled={timetable.timeSlots.length >= 10}
                        style={{
                          background:
                            timetable.timeSlots.length >= 10
                              ? '#d1d5db'
                              : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '10px 20px',
                          cursor: timetable.timeSlots.length >= 10 ? 'not-allowed' : 'pointer',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          boxShadow: timetable.timeSlots.length >= 10 ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)',
                          opacity: timetable.timeSlots.length >= 10 ? 0.6 : 1,
                        }}
                      >
                        <i className="pi pi-plus" style={{ fontSize: '14px' }}></i>
                        Add Period
                      </button>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      {timetable.timeSlots.map((slot, index) => {
                        const locked = !slot.isNew && !slot.isEditing;
                        const complete = isRowComplete(slot);

                        return (
                          <div
                            key={slot.id}
                            style={{
                              background: '#f9fafb',
                              borderRadius: '12px',
                              padding: '16px',
                              border: '2px solid #f1f5f9',
                              marginBottom: '12px',
                              animation: `fadeInSlide 0.3s ease-out ${index * 0.05}s backwards`,
                            }}
                          >
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '180px 150px 150px 200px 100px',
                                gap: '16px',
                                alignItems: 'start',
                              }}
                            >
                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    color: '#2855ae',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    marginBottom: '8px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  Period Label
                                </label>
                                <InputText
                                  value={slot.periodLabel}
                                  onChange={(e) => updateLocalField(timetable.id, slot.id, 'periodLabel', e.target.value)}
                                  disabled={locked || slot.saving}
                                  maxLength={10}
                                  style={{
                                    width: '100%',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '10px 12px',
                                    fontSize: '13px',
                                    height: '40px',
                                    boxSizing: 'border-box',
                                    background: locked ? '#f3f4f6' : 'white',
                                    color: locked ? '#6b7280' : 'inherit',
                                    cursor: locked ? 'not-allowed' : 'text',
                                  }}
                                />
                              </div>

                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    color: '#6b7280',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    marginBottom: '8px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  Start Time
                                </label>
                                <InputText
                                  type="time"
                                  value={slot.startTime}
                                  onChange={(e) => updateLocalField(timetable.id, slot.id, 'startTime', e.target.value)}
                                  disabled={locked || slot.saving}
                                  style={{
                                    width: '100%',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '10px 12px',
                                    fontSize: '13px',
                                    height: '40px',
                                    boxSizing: 'border-box',
                                    background: locked ? '#f3f4f6' : 'white',
                                    cursor: locked ? 'not-allowed' : 'text',
                                    color: locked ? '#6b7280' : 'inherit',
                                  }}
                                />
                              </div>

                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    color: '#6b7280',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    marginBottom: '8px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  End Time
                                </label>
                                <InputText
                                  type="time"
                                  value={slot.endTime}
                                  onChange={(e) => updateLocalField(timetable.id, slot.id, 'endTime', e.target.value)}
                                  disabled={locked || slot.saving}
                                  style={{
                                    width: '100%',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '10px 12px',
                                    fontSize: '13px',
                                    height: '40px',
                                    boxSizing: 'border-box',
                                    background: locked ? '#f3f4f6' : 'white',
                                    cursor: locked ? 'not-allowed' : 'text',
                                    color: locked ? '#6b7280' : 'inherit',
                                  }}
                                />
                              </div>

                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    color: '#6b7280',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    marginBottom: '8px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  Slot Type
                                </label>
                                <Dropdown
                                  value={slot.slotType}
                                  onChange={(e) => updateLocalField(timetable.id, slot.id, 'slotType', e.value)}
                                  options={slotTypeOptions}
                                  optionLabel="label"
                                  placeholder="Select type"
                                  disabled={locked || slot.saving}
                                  style={{
                                    width: '100%',
                                    height: '40px',
                                    background: locked ? '#f3f4f6' : 'white',
                                    cursor: locked ? 'not-allowed' : 'pointer',
                                  }}
                                  className="custom-dropdown"
                                />
                              </div>

                              <div style={{ marginTop: '21px', display: 'flex', gap: '8px' }}>
                                {slot.isNew ? (
                                  <button
                                    type="button"
                                    onClick={() => saveNewSlot(timetable.id, slot.id)}
                                    disabled={!complete || slot.saving}
                                    title="Save period"
                                    style={{
                                      background: !complete || slot.saving ? '#d1d5db' : '#22c55e',
                                      border: 'none',
                                      borderRadius: '8px',
                                      width: '40px',
                                      height: '40px',
                                      cursor: !complete || slot.saving ? 'not-allowed' : 'pointer',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <i
                                      className={`pi ${slot.saving ? 'pi-spin pi-spinner' : 'pi-check'}`}
                                      style={{ fontSize: '14px' }}
                                    ></i>
                                  </button>
                                ) : slot.isEditing ? (
                                  <button
                                    type="button"
                                    onClick={() => saveEditedSlot(timetable.id, slot.id)}
                                    disabled={!complete || slot.saving}
                                    title="Save changes"
                                    style={{
                                      background: !complete || slot.saving ? '#d1d5db' : '#22c55e',
                                      border: 'none',
                                      borderRadius: '8px',
                                      width: '40px',
                                      height: '40px',
                                      cursor: !complete || slot.saving ? 'not-allowed' : 'pointer',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <i
                                      className={`pi ${slot.saving ? 'pi-spin pi-spinner' : 'pi-check'}`}
                                      style={{ fontSize: '14px' }}
                                    ></i>
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => startEditing(timetable.id, slot.id)}
                                    title="Edit period"
                                    style={{
                                      background: '#3b82f6',
                                      border: 'none',
                                      borderRadius: '8px',
                                      width: '40px',
                                      height: '40px',
                                      cursor: 'pointer',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                                    }}
                                  >
                                    <i className="pi pi-pencil" style={{ fontSize: '14px' }}></i>
                                  </button>
                                )}

                                <button
                                  type="button"
                                  onClick={() =>
                                    slot.isEditing
                                      ? cancelEditing(timetable.id)
                                      : deleteTimeSlot(timetable.id, slot.id, slot.isNew, slot.periodLabel || 'this period')
                                  }
                                  title={slot.isEditing ? 'Cancel edit' : 'Deactivate period'}
                                  style={{
                                    background: '#ef4444',
                                    border: 'none',
                                    borderRadius: '8px',
                                    width: '40px',
                                    height: '40px',
                                    cursor: 'pointer',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
                                  }}
                                >
                                  <i className={`pi ${slot.isEditing ? 'pi-times' : 'pi-trash'}`} style={{ fontSize: '14px' }}></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </TabPanel>
            ))}
          </TabView>
        </div>
      )}
    </div>
  );
}